/**
 * Email template configurations for different error types
 */
const emailTemplates = {
  general_error: {
    subject: 'Support Request - Something Went Wrong',
    body: `Hi Support Team,\n\nI encountered an error while using the application.\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  post_regeneration_error: {
    subject: 'Post Regeneration Issue',
    body: ({ postId }) =>
      `Hello support,\n\nI encountered a regeneration error for Post ID: ${postId || 'N/A'}.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  profile_check_error: {
    subject: 'Profile Creation Issue',
    body: ({ websiteUrl }) =>
      `Hello Support Team,\n\nI faced an issue while creating my profile. \n\nWebsite URL: ${websiteUrl || 'Not Provided'} \n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  post_feedback: {
    subject: 'Feedback on Generated Post',
    body: ({ userInput, postId }) =>
      `Hello Support Team,\n\nI would like to provide the following feedback on a generated post: ${postId}\n\n${userInput.toString()}\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  post_schedule_error: {
    subject: 'Post Scheduling Issue',
    body: ({ postId }) =>
      `Hello Support Team,\n\nI encountered an issue while scheduling Post ID: ${postId || 'N/A'}.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  post_update_error: {
    subject: 'Post Update Issue',
    body: ({ postId }) =>
      `Hello Support Team,\n\nI encountered an issue while updating Post ID: ${postId || 'N/A'}.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  update_profile_summary_error: {
    subject: 'Profile Summary Update Issue',
    body: `Hello Support Team,\n\nI encountered an issue while updating my profile summary.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  update_offering_error: {
    subject: 'Offering Update Issue',
    body: `Hello Support Team,\n\nI encountered an issue while updating my offering.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  add_offering_error: {
    subject: 'Offering Addition Issue',
    body: `Hello Support Team,\n\nI encountered an issue while adding a new offering.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  delete_offering_error: {
    subject: 'Offering Deletion Issue',
    body: `Hello Support Team,\n\nI encountered an issue while deleting an offering.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  social_connect_error: {
    subject: 'Social Account Connection Issue',
    body: `Hello Support Team,\n\nI encountered an issue while connecting my social media account.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  social_disconnect_error: {
    subject: 'Social Account Disconnection Issue',
    body: `Hello Support Team,\n\nI encountered an issue while disconnecting my social media account.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  social_fetch_error: {
    subject: 'Social Accounts Loading Issue',
    body: `Hello Support Team,\n\nI encountered an issue while loading my social media accounts.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
  post_feedback_error: {
    subject: 'Post Feedback Sending Issue',
    body: ({ postId }) =>
      `Hello Support Team,\n\nI encountered an issue while sending feedback for Post ID: ${postId || 'N/A'}.\n\nCan you help me with this?\n\nThank you for your assistance!`,
    supportEmail: 'support@postreams.com',
  },
};

/**
 * Generate a mailto link with optional user email
 * @param {string} errorType - Type of error (key from emailTemplates)
 * @param {string} userEmail - Optional user email to include in the mailto
 * @param {string} websiteUrl - Optional website URL for profile check errors
 * @param {any} params - Optional parameters for dynamic content (e.g., postId)
 * @returns {string} - Complete mailto link
 */
export const generateMailtoLink = (
  errorType = 'general_error',
  userEmail = '',
  params = {
    postId: null,
    websiteUrl: null,
    userEmail: null,
  }
) => {
  const template = emailTemplates[errorType];
  if (!template) {
    console.warn(`Email template for "${errorType}" not found. Using general_error template.`);
    return generateMailtoLink('general_error', userEmail);
  }

  const subject = encodeURIComponent(template.subject);
  const body = encodeURIComponent(
    typeof template.body === 'function' ? template.body(params) : template.body
  );
  const email = template.supportEmail;

  const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}${
    userEmail ? `&from=${encodeURIComponent(userEmail)}` : ''
  }`;

  return mailtoLink;
};

/**
 * Get the subject and body for an error type without generating mailto link
 * Useful for custom implementations
 * @param {string} errorType - Type of error (key from emailTemplates)
 * @param {any} params - Optional parameters for dynamic content
 * @returns {object} - Object with subject, body, and supportEmail
 */
export const getEmailTemplate = (errorType = 'general_error', params = null) => {
  const template = emailTemplates[errorType];

  if (!template) {
    console.warn(`Email template for "${errorType}" not found. Using general_error template.`);
    return getEmailTemplate('general_error', params);
  }

  return {
    subject: template.subject,
    body: typeof template.body === 'function' ? template.body(params) : template.body,
    supportEmail: template.supportEmail,
  };
};

export default { generateMailtoLink, getEmailTemplate };
